import { useEffect, useRef, useState } from 'react'
import type { ChartPoint } from '@/shared/api/types'
import styles from './LineChart.module.css'

interface Props {
  points: ChartPoint[]
  /** Знак фиатной валюты для подписей осей и всплывашки. */
  sign: string
  height?: number
  /** Заголовок для читалки: у одной серии легенды нет, имя даёт заголовок. */
  label: string
}

/*
  Отступы графика — из макета (Overview, узел line-chart): колонка
  подписей слева 48 (из них 8 — зазор до сетки), сетка идёт до правого
  края карточки, сверху и снизу по 8, внизу ещё 24 под даты.
*/
const PAD = { top: 8, right: 0, bottom: 32, left: 48 }
/** Правый край подписей оси Y: 48 минус зазор 8. */
const Y_LABEL_X = PAD.left - 8
const TICKS = 6

/**
 * График дохода. Одна серия — значит без легенды: имя ей даёт заголовок
 * карточки, а цветом здесь ничего не различается.
 *
 * Числа для геометрии считаются из строк через `Number`, и это
 * единственное допустимое место такого преобразования во всём проекте:
 * результат идёт в координату пикселя, а не в сумму на экране.
 * Во всплывашке показывается ИСХОДНАЯ строка с сервера.
 */
/**
 * Подпись суммы со знаком валюты на своём месте.
 *
 * Доллар пишется перед числом, рубль — после. Общий шаблон
 * `${sign}${value}` давал «₽200k», чего по-русски не бывает
 * и что читается как чужая валюта.
 */
function withSign(value: string, sign: string): string {
  return sign === '$' ? `${sign}${value}` : `${value}\u00A0${sign}`
}

/**
 * Гладкая линия через все точки — как в макете.
 *
 * Интерполяция МОНОТОННАЯ, а не обычный сплайн, и это не вкусовщина.
 * Обычный сплайн между двумя соседними значениями выгибается за их
 * пределы: между доходом 0 и 100 линия провалится ниже нуля, а между
 * двумя равными точками нарисует горб. На графике денег это значит
 * показать мерчанту доход, которого не было. Монотонная схема
 * (Фрич — Карлсон) обнуляет наклон там, где направление меняется,
 * и линия не выходит за коридор между точками.
 */
export function smoothPath(pts: [number, number][]): string {
  if (pts.length === 0) return ''
  const first = pts[0] as [number, number]
  if (pts.length < 3) {
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ')
  }

  const n = pts.length
  const dx: number[] = []
  const slope: number[] = []
  for (let i = 0; i < n - 1; i++) {
    const a = pts[i] as [number, number]
    const b = pts[i + 1] as [number, number]
    const h = b[0] - a[0]
    dx.push(h)
    slope.push(h === 0 ? 0 : (b[1] - a[1]) / h)
  }

  const m: number[] = new Array(n).fill(0)
  m[0] = slope[0] ?? 0
  m[n - 1] = slope[n - 2] ?? 0
  for (let i = 1; i < n - 1; i++) {
    const s0 = slope[i - 1] ?? 0
    const s1 = slope[i] ?? 0
    // Разные знаки или ноль — вершина: наклон обнуляем, иначе линия
    // перелетит через точку и нарисует несуществующий пик.
    m[i] = s0 * s1 <= 0 ? 0 : (2 * s0 * s1) / (s0 + s1)
  }

  let d = `M${first[0].toFixed(1)} ${first[1].toFixed(1)}`
  for (let i = 0; i < n - 1; i++) {
    const a = pts[i] as [number, number]
    const b = pts[i + 1] as [number, number]
    const h = dx[i] ?? 0
    const c1x = a[0] + h / 3
    const c1y = a[1] + ((m[i] ?? 0) * h) / 3
    const c2x = b[0] - h / 3
    const c2y = b[1] - ((m[i + 1] ?? 0) * h) / 3
    d += ` C${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)},`
      + ` ${b[0].toFixed(1)} ${b[1].toFixed(1)}`
  }
  return d
}

export function LineChart({ points, sign, height = 400, label }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(720)
  const [hover, setHover] = useState<number | null>(null)

  // Ширину меряем, а не масштабируем через viewBox: при растяжении
  // вместе с графиком растянулись бы и подписи осей — на мобильном
  // они стали бы нечитаемо мелкими, на десктопе непропорционально
  // крупными.
  useEffect(() => {
    const node = wrapRef.current
    if (!node) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) setWidth(Math.max(280, entry.contentRect.width))
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  if (points.length === 0) {
    return (
      <div ref={wrapRef} className={styles.wrap}>
        <div className={styles.empty} style={{ height }}>
          Пока нет данных за этот период
        </div>
      </div>
    )
  }

  const values = points.map((p) => Number(p.amount))
  const max = Math.max(...values, 0)
  const min = Math.min(...values, 0)
  // Шкала строится от круглого ШАГА, а не от круглого максимума:
  // 6000 в пять делений даёт подписи $1.2k, $2.4k, $3.6k — числа,
  // которых никто не держит в голове. Круглый шаг даёт $1k, $2k, $3k.
  const step = niceStep((max === min ? max + 1 : max) - Math.min(min, 0), TICKS - 1)
  const bottom = min < 0 ? -Math.ceil(-min / step) * step : 0
  const top = bottom + Math.ceil(((max === min ? max + 1 : max) - bottom) / step) * step

  const plotW = width - PAD.left - PAD.right
  const plotH = height - PAD.top - PAD.bottom

  const x = (index: number) =>
    PAD.left + (points.length === 1 ? plotW / 2 : (index / (points.length - 1)) * plotW)
  const y = (value: number) =>
    PAD.top + plotH - ((value - bottom) / (top - bottom || 1)) * plotH

  const path = smoothPath(values.map((value, index) => [x(index), y(value)]))

  const yTicks: number[] = []
  for (let value = bottom; value <= top + step / 2; value += step) yTicks.push(value)
  // Подписи по X прореживаем: на месяце данных тридцать дат в ряд
  // сливаются в серую полосу.
  const labelStep = Math.max(1, Math.ceil(points.length / Math.max(1, Math.floor(plotW / 64))))

  const active = hover !== null ? points[hover] ?? null : null
  const activeValue = hover !== null ? values[hover] ?? null : null
  // Индекс наведения после проверок: дальше он точно число, и
  // подставлять `?? 0` в каждую координату не нужно.
  const at = hover ?? 0

  const pick = (clientX: number) => {
    const node = wrapRef.current
    if (!node) return
    const rect = node.getBoundingClientRect()
    const local = clientX - rect.left
    const ratio = (local - PAD.left) / (plotW || 1)
    const index = Math.round(ratio * (points.length - 1))
    setHover(Math.min(points.length - 1, Math.max(0, index)))
  }

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <svg
        className={styles.svg}
        width={width}
        height={height}
        role="img"
        aria-label={label}
        onMouseMove={(e) => pick(e.clientX)}
        onMouseLeave={() => setHover(null)}
        onTouchMove={(e) => { const t = e.touches[0]; if (t) pick(t.clientX) }}
        onTouchEnd={() => setHover(null)}
      >
        {yTicks.map((tick) => (
          <g key={tick}>
            <line className={styles.grid}
                  x1={PAD.left} x2={width - PAD.right} y1={y(tick)} y2={y(tick)} />
            <text className={`${styles.axis} ${styles.money}`}
                  x={Y_LABEL_X} y={y(tick)} textAnchor="end" dominantBaseline="middle">
              {withSign(shortMoney(tick), sign)}
            </text>
          </g>
        ))}

        {points.map((point, index) => (
          index % labelStep === 0 || index === points.length - 1 ? (
            <text key={point.date} className={styles.axis}
                  x={x(index)} y={height - 8}
                  /* Крайние подписи прижимаются к краю графика, иначе
                     первая уезжает под ось, а последняя обрезается. */
                  textAnchor={index === 0 ? 'start'
                    : index === points.length - 1 ? 'end' : 'middle'}>
              {shortDate(point.date)}
            </text>
          ) : null
        ))}

        <path className={styles.line} d={path} />

        {active && activeValue !== null ? (
          <g>
            <line className={styles.guide}
                  x1={x(at)} x2={x(at)} y1={PAD.top} y2={PAD.top + plotH} />
            <circle className={styles.dot} cx={x(at)} cy={y(activeValue)} r={5} />
          </g>
        ) : null}

        <rect className={styles.hit} x={PAD.left} y={PAD.top} width={plotW} height={plotH} />
      </svg>

      {active && activeValue !== null ? (
        <div className={styles.tip} style={{ left: x(at), top: y(activeValue) }}>
          {/* Исходная строка с сервера, а не пересчитанное число. */}
          <span className={styles.tipValue}>{withSign(active.amount, sign)}</span>
          <span className={styles.tipDate}>{longDate(active.date)}</span>
        </div>
      ) : null}

      {/* Те же данные таблицей: график читается глазами, а таблица —
          читалкой и при выключенной графике. */}
      <table className={styles.table}>
        <caption>{label}</caption>
        <thead><tr><th>Дата</th><th>Сумма</th></tr></thead>
        <tbody>
          {points.map((point) => (
            <tr key={point.date}>
              <td>{longDate(point.date)}</td>
              <td>{withSign(point.amount, sign)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Круглый шаг шкалы: 1, 2, 2.5 или 5, умноженные на степень десяти.
 *
 * Именно эти множители дают подписи, которые человек читает не думая:
 * $1k / $2k / $5k. Любой другой шаг рождает $1.2k и $3.6k.
 */
function niceStep(range: number, divisions: number): number {
  if (range <= 0 || divisions <= 0) return 1
  const rough = range / divisions
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const normalized = rough / magnitude
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10
  return nice * magnitude
}

/** «$8k» вместо «$8000» — так в макете и так подписи не наезжают. */
function shortMoney(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1000) return `${Math.round(value / 100) / 10}k`.replace('.0k', 'k')
  return String(Math.round(value * 100) / 100)
}

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн',
                'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']

function shortDate(iso: string): string {
  const [, month = '', day = ''] = iso.split('-')
  return `${day}.${month}`
}

function longDate(iso: string): string {
  const [, month = '01', day = '01'] = iso.split('-')
  const index = Number(month) - 1
  return `${Number(day)} ${MONTHS[index] ?? month}`
}
